/**
 * JobDetailHeader - Shows job title, company, meta info, and match score.
 */

import type { PipelineJob } from '@/types/pipeline';

interface JobDetailHeaderProps {
  job: PipelineJob;
}

/**
 * Format an ISO date string to a concise relative date.
 */
function formatRelativeDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return date.toLocaleDateString();
}

function getScoreClass(score: number): string {
  if (score >= 70) return 'job-detail-header__score--high';
  if (score >= 50) return 'job-detail-header__score--medium';
  return 'job-detail-header__score--low';
}

export function JobDetailHeader({ job }: JobDetailHeaderProps) {
  const scoreClass = getScoreClass(job.matchScore);

  return (
    <div className="job-detail-header">
      <div className="job-detail-header__top-row">
        <div className="job-detail-header__info">
          <h1 className="job-detail-header__title">{job.title}</h1>
          <p className="job-detail-header__company">{job.company}</p>

          <div className="job-detail-header__meta">
            {job.location && (
              <span className="job-detail-header__meta-item">
                <svg
                  className="job-detail-header__meta-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                {job.location}
              </span>
            )}

            {job.source && (
              <span className="job-detail-header__source-badge">{job.source}</span>
            )}

            {job.url && (
              <a
                href={job.url}
                target="_blank"
                rel="noopener noreferrer"
                className="job-detail-header__url-link"
                title={job.url}
              >
                <svg
                  className="job-detail-header__meta-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                View original
              </a>
            )}
          </div>

          <p className="job-detail-header__date">
            Added {formatRelativeDate(job.addedAt)}
            {job.appliedAt && ` \u00B7 Applied ${formatRelativeDate(job.appliedAt)}`}
            {job.interviewAt && ` \u00B7 Interview ${formatRelativeDate(job.interviewAt)}`}
          </p>
        </div>

        {job.matchScore > 0 && (
          <div className={`job-detail-header__score ${scoreClass}`}>
            <span className="job-detail-header__score-value">{job.matchScore}%</span>
            <span className="job-detail-header__score-label">Match</span>
          </div>
        )}
      </div>
    </div>
  );
}
